import React, { useRef, useMemo, useState, useEffect } from "react"

import {
  colors,
  IconEmailSmall,
  IconIdCardSmall,
  IconLockSmall,
  IconPlusSmall,
  IconPhoneSmall,
} from "@izettle/otto"
import { Button, Text } from "@izettle/react"
import { useLocalization } from "@izettle/tenko-react"

// import { Accordion } from "@/core/components/accordion"
import { useModal } from "@/core/components/modal"
import {
  Container,
  Content,
  Title1,
  SvgIcon,
  Footer,
  AnchorButton,
  Item,
  Divider,
  Anchor,
} from "@/core/components/shared"
import { useSlider } from "@/core/components/slider"
import { PaypalPrivacyStatementLink } from "@/core/utils/constant"
import { FailureMessage, Phone } from "@/modules/sign-up/api/types"
import { useSignUpPost } from "@/modules/sign-up/api/use-sign-up"
import ErrorDialog, {
  ErrorDialogMessageProps,
} from "@/modules/sign-up/components/error-dialog"
import {
  PrivacyPolicyFrom,
  TrackingAction,
  useSignUpTracking,
} from "@/modules/sign-up/hooks/tracking-sign-up"
import formatPhone from "@/modules/sign-up/shared/phone-format"

import { SlideIndexs, Modals } from "./types"

export type ConfirmProps = {
  orgUuid: string
  purchaseUuid: string
  orgName: string
  firstName: string
  lastName: string
  email: string
  phone?: Phone
  orgPrivacyPolicyLink?: string
  onClickName: () => void
  setCustomerUuid: (_customerId: string) => void
}

export const Confirm = ({
  orgName,
  firstName,
  lastName,
  email,
  phone,
  orgPrivacyPolicyLink,
  orgUuid,
  purchaseUuid,
  onClickName,
  setCustomerUuid,
}: ConfirmProps) => {
  const ref = useRef(null)
  const { text } = useLocalization()
  const [dialogMessage, setDialogMessage] = useState<ErrorDialogMessageProps>({
    message: "",
  })

  const customerName = `${firstName} ${lastName}`

  const { goNext, goTo, index } = useSlider()
  const eleRef = useRef<HTMLButtonElement>(null)

  const { mutate, isLoading, data, reset } = useSignUpPost({
    onSuccess: (successData) => {
      if (successData?.uuid) {
        setCustomerUuid(successData?.uuid)
        goNext({ customerUuid: successData?.uuid })
      }
    },
  })

  const [showPolicyModal] = useModal(Modals.Policy)
  const { track } = useSignUpTracking(purchaseUuid)

  useEffect(() => {
    if (index === SlideIndexs.Confirm) {
      track(TrackingAction.ViewedConfirm)
    }
  }, [index])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePolicyClicked = () => {
    showPolicyModal(ref)
    track(TrackingAction.ViewedPrivacyPolicy, {
      page: PrivacyPolicyFrom.SignUpPage,
      sellerHasPrivacyPolicy: !!orgPrivacyPolicyLink,
    })
  }

  if (data?.failureMessage) {
    switch (data?.failureMessage) {
      case FailureMessage.PURCHASE_ALREADY_ASSOCIATED:
        setDialogMessage({ message: "SIGNUP_ERROR_SALE_ALREADY_ASSIGNED" })
        break
      case FailureMessage.EMAIL_OR_PHONE_ALREADY_EXISTS:
        setDialogMessage({
          title: "SIGNUP_ERROR_EMAIL_OR_PHONE_ALREADY_EXISTS_TITLE",
          message: "SIGNUP_ERROR_EMAIL_OR_PHONE_ALREADY_EXISTS",
        })
        break
      case FailureMessage.INVALID_INPUT:
      case FailureMessage.OPERATION_ERROR:
      default:
        setDialogMessage({ message: "SIGNUP_ERROR_OPERATION_ERROR" })
        break
    }
    reset()
  }

  const closeDialog = () => {
    setDialogMessage({ message: "" })
  }

  const handleNameClick = () => {
    onClickName()
    goTo(SlideIndexs.FirstName, { customerGoBack: SlideIndexs.Confirm })
  }
  const handleEmailClick = () => {
    goTo(SlideIndexs.Email, { customerGoBack: SlideIndexs.Confirm })
  }
  const handlePhoneClick = () => {
    goTo(SlideIndexs.Phone, { customerGoBack: SlideIndexs.Confirm })
  }

  const handleConfirm = () => {
    mutate({
      orgUuid,
      purchaseUuid,
      customerInfo: {
        firstName,
        lastName,
        email,
        phone,
      },
    })
    track(TrackingAction.ClickedConfirm, {
      withEmailAddress: !!email,
      withPhoneNumber: !!phone?.number,
    })
  }

  const formatedPhone = useMemo(
    () => formatPhone(phone) ?? text("SIGNUP_ADD_PHONE_NUMBER_OPTIONAL"),
    [phone],
  )

  return (
    <Container>
      <Content>
        <Title1>{text("SIGNUP_CONFIRM_YOUR_DETAILS")}</Title1>
        <Text.RegularSecondary>{text("SIGNUP_TAP_TO_EDIT")}</Text.RegularSecondary>
        <Item>
          <SvgIcon
            html={IconIdCardSmall.svg}
            color={colors.action.primary.border.default}
            margin="0 16px 0 0"
          />
          <AnchorButton ref={eleRef} onClick={handleNameClick}>
            {customerName}
          </AnchorButton>
        </Item>
        <Item>
          <SvgIcon
            html={IconEmailSmall.svg}
            color={colors.action.primary.border.default}
            margin="0 16px 0 0"
          />
          <AnchorButton onClick={handleEmailClick}>{email}</AnchorButton>
        </Item>
        <Item>
          <SvgIcon
            html={phone ? IconPhoneSmall.svg : IconPlusSmall.svg}
            color={colors.action.primary.border.default}
            margin="0 16px 0 0"
          />
          <AnchorButton onClick={handlePhoneClick}>{formatedPhone}</AnchorButton>
        </Item>
        <Item>
          <Divider />
        </Item>
        {/* <Accordion title={text("SIGNUP_TERMS_AND_CONDISTIONS_TITLE")}>
          <Text.SmallSecondary>
            {text("SIGNUP_TERMS_AND_CONDISTIONS_TEXT", [orgName])}
          </Text.SmallSecondary>
        </Accordion> */}
        {/* <Item ref={ref}>
          <SvgIcon
            html={IconLockSmall.svg}
            color={colors.action.primary.border.default}
            margin="0 16px 0 0"
          />
          <AnchorButton className="small" size="small" onClick={handlePolicyClicked}>
            {text("SIGNUP_LEARN_HOW_YOUR_INFORMATION_IS_HANDLED")}
          </AnchorButton>
        </Item> */}
        <Item>
          <SvgIcon
            html={IconLockSmall.svg}
            color={colors.action.primary.border.default}
            margin="0 16px 0 0"
          />
          <Anchor href={PaypalPrivacyStatementLink} target="_blank">
            {text("SIGNUP_PRIVACY_POLICY_PAYPAL_PRIVACY_STATEMENT")}
          </Anchor>
        </Item>
        <Item>
          <Text.SmallSecondary>
            {text("SIGNUP_TERMS_AND_CONDISTIONS_TEXT_2", [orgName])}
          </Text.SmallSecondary>
        </Item>
      </Content>
      <Footer>
        <Button size="large" fullWidth onClick={handleConfirm} disabled={isLoading}>
          {text("SIGNUP_CONFIRM_AND_SIGN_UP")}
        </Button>
      </Footer>
      <ErrorDialog
        onClose={closeDialog}
        message={dialogMessage.message}
        title={dialogMessage.title}
      />
    </Container>
  )
}
